// src/services/cotizadorCalc.js
// Cotizador Boa Vista → Manaus → destino (solo admin). Funciones puras, sin
// UI ni Supabase: replican las fórmulas de Calculadora-final-v2.xlsx para que
// den exactamente los mismos números. Se redondea solo al mostrar.
//
// Fechas = 'YYYY-MM-DD', horas = 'HH:MM' (lo que devuelven los pickers
// nativos). Internamente todo se calcula en minutos enteros para que
// floor() no falle en los bordes por culpa de decimales flotantes.

const MIN_DIA = 1440;

export const AJUSTES_DEFECTO = {
  hostal_dia: 100,
  cargo_mediodia: 100,
  comision_brt: 0.1,
  tarifa_pct: 0.813,
  factor_comprarviagem: 0.9021,
  ganancia_minima: 200,
  omnibus_precio: 250,
  margen_manaus_horas: 4,
  hora_vuelo_defecto: '15:00',
  milheiro_defecto: 16,
  taxa_defecto: 55.17,
  niveles: [100, 200, 300, 400, 500],
  usd_defecto: 600,
  tasa_defecto: 4.85,
  margen_conexion_horas: 4,
  ventana_historial_dias: 21,
  particular_margen_pct: 10, // clientes que escriben directo (ni directos de hostal ni revendedores)
};

// Tipos de cliente cuyo precio sale de "costo + margen" (sin hostal): revendedor y particular.
export const usaMargen = (tipo) => tipo === 'revendedor' || tipo === 'particular';

// Valores de un revendedor nuevo (adenda "Revendedor").
export const REVENDEDOR_DEFECTO = { margen_pct: 7, ganancia_minima: 0, redondeo: 1 };

export const HORARIOS_DEFECTO = [
  { nombre: '06:00 mañana', sale: '06:00', llega: '17:30', mas_dias: 0, precio: null, activo: true, nota: 'Amatur' },
  { nombre: '09:00 mañana', sale: '09:00', llega: '22:00', mas_dias: 0, precio: null, activo: true, nota: 'Eucatur / Capital do Café' },
  { nombre: '18:00 noche', sale: '18:00', llega: '06:30', mas_dias: 1, precio: null, activo: true, nota: 'Algunos son convencional' },
  { nombre: '19:00 noche', sale: '19:00', llega: '07:00', mas_dias: 1, precio: null, activo: true, nota: '' },
  { nombre: '21:00 noche', sale: '21:00', llega: '09:10', mas_dias: 1, precio: null, activo: true, nota: '' },
];

// ── Utilidades de fecha / hora ──────────────────────────────────────
export const esNum = (v) => v !== null && v !== undefined && v !== '' && !Number.isNaN(Number(v));
const num = (v) => (esNum(v) ? Number(v) : null);
const n0 = (v) => (esNum(v) ? Number(v) : 0);

export function diaNum(iso) {
  if (!iso) return null;
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return Math.floor(Date.UTC(y, m - 1, d) / 86400000);
}

export function isoDeDiaNum(n) {
  return new Date(n * 86400000).toISOString().slice(0, 10);
}

export function sumarDias(iso, dias) {
  const n = diaNum(iso);
  return n === null ? null : isoDeDiaNum(n + dias);
}

export function minDeHora(hhmm) {
  if (!hhmm) return null;
  const [h, m] = String(hhmm).split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

// minutos absolutos de una fecha+hora (para comparar tramos entre sí)
export function minAbs(fechaIso, hhmm) {
  const d = diaNum(fechaIso);
  const m = minDeHora(hhmm);
  return d === null || m === null ? null : d * MIN_DIA + m;
}

export function fmtFecha(iso) {
  if (!iso) return '';
  const [, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}`;
}

export function fmtHoraAbs(abs) {
  if (abs === null || abs === undefined) return '';
  const dia = Math.floor(abs / MIN_DIA);
  const resto = abs - dia * MIN_DIA;
  const hh = String(Math.floor(resto / 60)).padStart(2, '0');
  const mm = String(resto % 60).padStart(2, '0');
  return `${fmtFecha(isoDeDiaNum(dia))} ${hh}:${mm}`;
}

// R$ con coma decimal: 2.910,00 · decimales = 0 para ganancia y topes
export function fmtBRL(v, decimales = 2) {
  if (v === null || v === undefined || Number.isNaN(v)) return '';
  return Number(v).toLocaleString('pt-BR', {
    minimumFractionDigits: decimales, maximumFractionDigits: decimales,
  });
}

// ── Ómnibus Boa Vista → Manaus ──────────────────────────────────────
export function elegirOmnibus({ llegada, diaVuelo, horaVuelo, ajustes, horarios }) {
  const llegadaN = diaNum(llegada);
  const diaN = diaNum(diaVuelo);
  if (llegadaN === null || diaN === null) return null;

  const T = minDeHora(horaVuelo) ?? minDeHora(ajustes.hora_vuelo_defecto) ?? 15 * 60;
  const margenMin = Math.round(n0(ajustes.margen_manaus_horas) * 60);

  let mejor = null;
  horarios.forEach((h, i) => {
    if (h.activo === false || !h.sale || !h.llega) return;
    const aMin = minDeHora(h.llega);
    const sMin = minDeHora(h.sale);
    // Día de salida más tarde que todavía llega a tiempo (floor con negativos).
    const salidaN = diaN + Math.floor((T - margenMin - n0(h.mas_dias) * MIN_DIA - aMin) / MIN_DIA);
    // El ómnibus sale como mínimo al día siguiente de la llegada.
    if (salidaN <= llegadaN) return;
    const cargo = sMin > 720 ? n0(ajustes.cargo_mediodia) : 0;
    const hostal = (salidaN - llegadaN) * n0(ajustes.hostal_dia) + cargo;
    const total = esNum(h.precio) ? Number(h.precio) : n0(ajustes.omnibus_precio);
    const costo = hostal + total;
    // Empate: gana el primero de la lista (comparación estricta).
    if (!mejor || costo < mejor.costo) {
      mejor = { indice: i, salidaIso: isoDeDiaNum(salidaN), sale: h.sale, hostal, total, costo, nombre: h.nombre };
    }
  });
  return mejor;
}

// Ómnibus para un revendedor: no hay hostal ni "día de llegada", así que se
// calcula el día de salida de cada horario activo con la misma fórmula y se
// elige el de menor precio Total. Empate: gana el primero de la lista.
export function elegirOmnibusRevendedor({ diaVuelo, horaVuelo, ajustes, horarios }) {
  const diaN = diaNum(diaVuelo);
  if (diaN === null) return null;
  const T = minDeHora(horaVuelo) ?? minDeHora(ajustes.hora_vuelo_defecto) ?? 15 * 60;
  const margenMin = Math.round(n0(ajustes.margen_manaus_horas) * 60);

  let mejor = null;
  horarios.forEach((h, i) => {
    if (h.activo === false || !h.sale || !h.llega) return;
    const aMin = minDeHora(h.llega);
    const salidaN = diaN + Math.floor((T - margenMin - n0(h.mas_dias) * MIN_DIA - aMin) / MIN_DIA);
    const total = esNum(h.precio) ? Number(h.precio) : n0(ajustes.omnibus_precio);
    if (!mejor || total < mejor.total) {
      mejor = { indice: i, salidaIso: isoDeDiaNum(salidaN), sale: h.sale, hostal: 0, total, costo: total, nombre: h.nombre };
    }
  });
  return mejor;
}

// Vuelo que sale de Boa Vista (sale_de = 'BVB'): no hay ómnibus ni espera en Manaus. El hostal
// es el mismo de siempre: una noche por cada día entre la llegada y el día del vuelo, más el
// cargo de mediodía si el vuelo sale después de las 12:00 (y hubo al menos una noche).
// Devuelve null si el vuelo sale antes de que el cliente llegue.
export function hostalSalidaBoaVista({ llegada, diaVuelo, horaVuelo, ajustes }) {
  const llegadaN = diaNum(llegada);
  const diaN = diaNum(diaVuelo);
  if (llegadaN === null || diaN === null || diaN < llegadaN) return null;
  const noches = diaN - llegadaN;
  const T = minDeHora(horaVuelo) ?? minDeHora(ajustes.hora_vuelo_defecto) ?? 15 * 60;
  const cargo = noches > 0 && T > 720 ? n0(ajustes.cargo_mediodia) : 0;
  return { noches, hostal: noches * n0(ajustes.hostal_dia) + cargo };
}
export const saleDeBoaVista = (op) => op?.sale_de === 'BVB';

// Datos del ómnibus elegido para la tarjeta del cliente (orçamento).
export function infoOmnibus(omn, horarios) {
  if (!omn) return null;
  const h = horarios[omn.indice] || {};
  const salidaN = diaNum(omn.salidaIso);
  return {
    indice: omn.indice, salidaIso: omn.salidaIso, sale: h.sale || omn.sale,
    llegaIso: isoDeDiaNum(salidaN + n0(h.mas_dias)), llega: h.llega || null,
    empresa: h.empresa || '', terminalOrigen: h.terminal_origen || '', terminalDestino: h.terminal_destino || '',
  };
}

// ── Comisión BRT ────────────────────────────────────────────────────
export function comisionBRT(total, ajustes) {
  return n0(total) * n0(ajustes.tarifa_pct) * n0(ajustes.comision_brt);
}

// ── Costo real por fuente + mejor fuente ────────────────────────────
const FUENTES = ['Comprarviagem', 'Speedmilhas', 'Resumen del pedido', 'Millas'];

export function costosFuentes(op, ajustes) {
  const lista = num(op.comprarviagem_lista);
  const costos = [
    lista === null ? null : lista * n0(ajustes.factor_comprarviagem),
    num(op.speedmilhas),
    num(op.resumen_pedido),
    null,
  ];
  const millas = num(op.millas_cantidad);
  if (millas !== null) {
    const milheiro = esNum(op.milheiro) ? Number(op.milheiro) : n0(ajustes.milheiro_defecto);
    const taxa = esNum(op.taxa) ? Number(op.taxa) : n0(ajustes.taxa_defecto);
    costos[3] = (millas / 1000) * milheiro + taxa;
  }
  const llenos = costos.filter((c) => c !== null);
  if (!llenos.length) return { costos, mejorCosto: null, mejorFuente: null };
  const mejorCosto = Math.min(...llenos);
  // Empate: orden Comprarviagem, Speedmilhas, Resumen, Millas.
  const mejorFuente = FUENTES[costos.findIndex((c) => c === mejorCosto)];
  return { costos, mejorCosto, mejorFuente };
}

// ── Día de llegada del vuelo ────────────────────────────────────────
// Cuántos días después del día de salida llega el vuelo: 0 = el mismo día, 1 = al día
// siguiente… Si la opción lo trae escrito (llegada_mas_dias) manda ese dato. Si no, se
// adivina como antes: hora de llegada menor que la de salida = día siguiente. Esa regla
// falla con un vuelo 08:15 -> 10:35 del día siguiente (parece de 2 h), por eso existe el campo.
export function masDiasLlegada(op, horaSalidaDefecto) {
  if (esNum(op.llegada_mas_dias)) return Math.max(0, Math.floor(Number(op.llegada_mas_dias)));
  const s = minDeHora(op.hora_vuelo) ?? minDeHora(horaSalidaDefecto);
  const l = minDeHora(op.hora_llegada_vuelo);
  return s !== null && l !== null && l < s ? 1 : 0;
}

// Duración aproximada del vuelo en minutos, con las horas locales tal como salen en
// pantalla (Manaus y el destino pueden tener 1 h de diferencia). Solo para ti: no va
// en la imagen del cliente.
export function duracionVueloMin(op) {
  const s = minDeHora(op.hora_vuelo);
  const l = minDeHora(op.hora_llegada_vuelo);
  if (s === null || l === null) return null;
  return masDiasLlegada(op) * MIN_DIA + l - s;
}

export function fmtDuracion(min) {
  if (min === null || min === undefined || min < 0) return '';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

// ── Fechas con el año equivocado ────────────────────────────────────
// Una fecha de vuelo muy anterior a la llegada del cliente (o más de un año después) casi siempre
// es un año mal puesto (por ejemplo 06/11/2024 en vez de 2026): el día de la semana sale mal y todo
// lo demás se calcula sobre un día que no existe. Se marca para que se corrija.
export function fechaSospechosa(diaVuelo, llegada) {
  const d = diaNum(diaVuelo);
  const l = diaNum(llegada);
  return d !== null && l !== null && (d < l - 30 || d > l + 400);
}

// Misma fecha (día y mes) con el año que corresponde a la llegada: el de la llegada, o el siguiente
// si así quedaría más de medio año antes (llegada en diciembre, vuelo en enero).
export function anioCorregido(diaVuelo, llegada) {
  const d = String(diaVuelo || '').slice(5, 10);
  const l = diaNum(llegada);
  if (!/^\d{2}-\d{2}$/.test(d) || l === null) return null;
  const anio = Number(String(llegada).slice(0, 4));
  const candidata = (y) => { const iso = `${y}-${d}`; const n = diaNum(iso); return n !== null && isoDeDiaNum(n) === iso ? iso : null; };
  const a = candidata(anio);
  if (a && diaNum(a) < l - 180) return candidata(anio + 1) || a;
  return a;
}

// ── Tramos extra (ómnibus después del vuelo) ────────────────────────
// Solo informativos: no cambian la ganancia (no hay hostal fuera de Boa
// Vista; si el cliente espera, espera en rodoviaria/aeropuerto). El precio
// de cada ómnibus extra SÍ resta de la ganancia -- eso va en calcularOpcion.
export function validarExtras(op, ajustes) {
  const margenMin = Math.round(n0(ajustes.margen_conexion_horas) * 60);
  const extras = Array.isArray(op.extras) ? op.extras : [];

  // Llegada del vuelo: si la hora de llegada es menor que la de salida de
  // Manaus, cruzó la medianoche y llega al día siguiente.
  let llegaAbs = null;
  const hLlega = minDeHora(op.hora_llegada_vuelo);
  const dV = diaNum(op.dia_vuelo);
  if (dV !== null && hLlega !== null) {
    llegaAbs = dV * MIN_DIA + hLlega + masDiasLlegada(op, ajustes.hora_vuelo_defecto) * MIN_DIA;
  }

  let prevLlega = llegaAbs;
  const tramos = extras.map((ex) => {
    const desde = prevLlega === null ? null : prevLlega + margenMin;
    const sale = minAbs(ex.sale_fecha, ex.sale_hora);
    const llega = minAbs(ex.llega_fecha, ex.llega_hora);
    let ok = null;
    let esperaMin = null;
    if (desde !== null && sale !== null) {
      ok = sale >= desde;
      esperaMin = sale - prevLlega;
    }
    prevLlega = llega;
    return { desde, ok, esperaMin };
  });

  return { llegadaVueloAbs: llegaAbs, tramos, primerDesde: llegaAbs === null ? null : llegaAbs + margenMin };
}

// ── Precio de venta a un revendedor ─────────────────────────────────
// Sugerido = el mayor entre costo ÷ (1 − margen) y costo + ganancia mínima,
// subido al múltiplo de `redondeo`. El 1e-9 evita que 2.005,0000000002 suba
// un escalón entero por culpa de los decimales flotantes.
export function precioSugeridoRevendedor(costo, { margen_pct, ganancia_minima, redondeo }) {
  const m = n0(margen_pct) / 100;
  const porMargen = m < 1 ? costo / (1 - m) : Infinity;
  const crudo = Math.max(porMargen, costo + n0(ganancia_minima));
  const paso = n0(redondeo) > 0 ? Number(redondeo) : 1;
  return Math.ceil(crudo / paso - 1e-9) * paso;
}

// Margen, ganancia mínima y redondeo de un cliente de "costo + margen". El revendedor los trae
// en su ficha; el particular, de su propia cotización (margen_pct, ganancia_minima) o de Ajustes
// (10 % por defecto).
export function reglaPrecio(cotizacion, aj) {
  if (cotizacion.tipo === 'particular') {
    return {
      margen_pct: esNum(cotizacion.margen_pct) ? Number(cotizacion.margen_pct) : n0(aj.particular_margen_pct),
      ganancia_minima: esNum(cotizacion.ganancia_minima) ? Number(cotizacion.ganancia_minima) : 0,
      redondeo: 1,
    };
  }
  return { ...REVENDEDOR_DEFECTO, ...(cotizacion.revendedor || {}) };
}

function calcularOpcionRevendedor({ cotizacion, opcion, ajustes: aj, horarios }) {
  const rev = reglaPrecio(cotizacion, aj);
  const gananciaMin = esNum(opcion.ganancia_minima_opcion) ? Number(opcion.ganancia_minima_opcion) : n0(rev.ganancia_minima);

  const bvb = saleDeBoaVista(opcion);
  const omn = bvb ? null : elegirOmnibusRevendedor({ diaVuelo: opcion.dia_vuelo, horaVuelo: opcion.hora_vuelo, ajustes: aj, horarios });
  const totalManual = num(opcion.omnibus_total_manual);
  const omnibusTotal = bvb ? 0 : omn ? (totalManual !== null ? totalManual : omn.total) : null;
  const omnibusEleg = bvb ? 'Vuela desde Boa Vista' : omn ? `Sale ${fmtFecha(omn.salidaIso)} ${omn.sale}` : null;
  const comision = omnibusTotal === null ? null : comisionBRT(omnibusTotal, aj);
  const omnibusNeto = omnibusTotal === null ? null : omnibusTotal - comision;

  const extras = Array.isArray(opcion.extras) ? opcion.extras : [];
  const extraTotales = extras.map((e) => n0(e.total));
  const extraComisiones = extraTotales.map((t) => comisionBRT(t, aj));
  const extraComision = extraComisiones.reduce((a, b) => a + b, 0);
  const extraNeto = extraTotales.reduce((a, b) => a + b, 0) - extraComision + n0(opcion.otro_extra);

  const { costos, mejorCosto, mejorFuente } = costosFuentes(opcion, aj);

  const costoTotal = omnibusNeto === null || mejorCosto === null ? null : omnibusNeto + extraNeto + mejorCosto;
  const sugerido = costoTotal === null ? null : precioSugeridoRevendedor(costoTotal, { ...rev, ganancia_minima: gananciaMin });
  const manual = num(opcion.precio_final_manual);
  const precioFinal = manual !== null ? manual : sugerido;
  const ganancia = costoTotal === null || precioFinal === null ? null : precioFinal - costoTotal;
  const gananciaPct = ganancia === null || !precioFinal ? null : ganancia / precioFinal;

  let estado = null;
  if (!omn && !bvb) estado = 'sin_omnibus';
  else if (ganancia !== null) {
    const umbral = Math.max((n0(rev.margen_pct) / 100) * precioFinal, gananciaMin);
    estado = ganancia < 0 ? 'pierdes' : ganancia < umbral ? 'poca' : 'bien';
  }

  // Hasta cuánto pagar por el vuelo, partiendo del precio final de la opción.
  const base = precioFinal === null || omnibusNeto === null ? null : precioFinal - omnibusNeto - extraNeto;
  const topes = (aj.niveles || []).map((G) => {
    const costoMax = base === null ? null : base - G;
    return { nivel: G, costoMax, listaMax: costoMax === null ? null : costoMax / (n0(aj.factor_comprarviagem) || 1) };
  });

  return {
    tipo: 'revendedor', gananciaMin, margenPct: n0(rev.margen_pct),
    bus: infoOmnibus(omn, horarios), saleBVB: bvb, omnibusEleg, hostal: 0, omnibusTotal, comision, omnibusNeto,
    extraComisiones, extraComision, extraNeto,
    costos, mejorCosto, mejorFuente,
    costoTotal, sugerido, precioFinal, ganancia, gananciaPct, estado, topes,
    conexion: validarExtras(opcion, aj),
  };
}

// ── Cálculo completo de una opción de vuelo ─────────────────────────
export function calcularOpcion({ cotizacion, opcion, ajustes, horarios }) {
  const aj = { ...AJUSTES_DEFECTO, ...ajustes };
  if (usaMargen(cotizacion.tipo)) return calcularOpcionRevendedor({ cotizacion, opcion, ajustes: aj, horarios });
  const usd = num(cotizacion.usd);
  const tasa = num(cotizacion.tasa);
  const R = usd === null || tasa === null ? null : usd * tasa;
  const gananciaMin = esNum(cotizacion.ganancia_minima) ? Number(cotizacion.ganancia_minima) : aj.ganancia_minima;

  const bvb = saleDeBoaVista(opcion);
  const omn = bvb ? null : elegirOmnibus({
    llegada: cotizacion.llegada, diaVuelo: opcion.dia_vuelo, horaVuelo: opcion.hora_vuelo, ajustes: aj, horarios,
  });
  const hvb = bvb ? hostalSalidaBoaVista({ llegada: cotizacion.llegada, diaVuelo: opcion.dia_vuelo, horaVuelo: opcion.hora_vuelo, ajustes: aj }) : null;

  const totalManual = num(opcion.omnibus_total_manual);
  let hostal = null;
  let omnibusTotal = null;
  let omnibusEleg = null;
  if (omn) {
    hostal = omn.hostal;
    omnibusTotal = totalManual !== null ? totalManual : omn.total;
    omnibusEleg = `Sale ${fmtFecha(omn.salidaIso)} ${omn.sale}`;
  } else if (hvb) {
    hostal = hvb.hostal;
    omnibusTotal = 0; // sin ómnibus Boa Vista → Manaus
    omnibusEleg = 'Vuela desde Boa Vista';
  }
  const comision = omnibusTotal === null ? null : comisionBRT(omnibusTotal, aj);
  const omnibusNeto = omnibusTotal === null ? null : omnibusTotal - comision;

  // Ómnibus extra (varios): cada uno lleva su comisión BRT; "otro extra" va sin BRT.
  const extras = Array.isArray(opcion.extras) ? opcion.extras : [];
  const extraTotales = extras.map((e) => n0(e.total));
  const extraComisiones = extraTotales.map((t) => comisionBRT(t, aj));
  const extraComision = extraComisiones.reduce((a, b) => a + b, 0);
  const extraBrutos = extraTotales.reduce((a, b) => a + b, 0);
  const otroExtra = n0(opcion.otro_extra);
  const extraNeto = extraBrutos - extraComision + otroExtra;

  const { costos, mejorCosto, mejorFuente } = costosFuentes(opcion, aj);

  const base = R === null || omnibusNeto === null ? null : R - hostal - omnibusNeto - extraNeto;
  const ganancia = base === null || mejorCosto === null ? null : base - mejorCosto;

  let estado = null;
  if (bvb && !hvb) estado = 'dia_invalido'; // el vuelo sale antes de que llegue el cliente
  else if (!omn && !bvb) estado = 'sin_omnibus';
  else if (ganancia !== null) estado = ganancia < 0 ? 'pierdes' : ganancia < gananciaMin ? 'poca' : 'bien';

  // Hasta cuánto pagar por el vuelo (se calcula aunque no haya precio).
  const topes = (aj.niveles || []).map((G) => {
    const costoMax = base === null ? null : base - G;
    return { nivel: G, costoMax, listaMax: costoMax === null ? null : costoMax / (n0(aj.factor_comprarviagem) || 1) };
  });

  return {
    R, gananciaMin,
    bus: infoOmnibus(omn, horarios), saleBVB: bvb, noches: hvb ? hvb.noches : null,
    omnibusEleg, hostal, omnibusTotal, comision, omnibusNeto,
    extraComisiones, extraComision, extraNeto,
    costos, mejorCosto, mejorFuente,
    ganancia, estado, topes,
    conexion: validarExtras(opcion, aj),
  };
}

// ── Recorrido que ve el cliente (para la imagen del paquete) ─────────
// Llegada a Boa Vista → N noches de hostal → ómnibus a Manaus → vuelo (→ ómnibus extra).
// Solo datos del viaje: nada de costos ni ganancia. Devuelve null si la opción no
// se puede armar (sin día/hora de vuelo, o ningún ómnibus llega a tiempo a Manaus).
export function itinerarioOpcion({ cotizacion, opcion, ajustes, horarios }) {
  if (!opcion?.dia_vuelo || !opcion?.hora_vuelo) return null;
  if (saleDeBoaVista(opcion)) return null; // este itinerario viejo parte del ómnibus; para BVB usa el orçamento
  const aj = { ...AJUSTES_DEFECTO, ...ajustes };
  const omn = elegirOmnibus({
    llegada: cotizacion.llegada, diaVuelo: opcion.dia_vuelo, horaVuelo: opcion.hora_vuelo, ajustes: aj, horarios,
  });
  if (!omn) return null;
  const h = horarios[omn.indice];
  const salidaN = diaNum(omn.salidaIso);
  const extras = (Array.isArray(opcion.extras) ? opcion.extras : [])
    .filter((e) => e && (e.ciudad || e.sale_fecha || e.sale_hora))
    .map((e) => ({
      ciudad: e.ciudad || '', saleFecha: e.sale_fecha || null, saleHora: e.sale_hora || null,
      llegaFecha: e.llega_fecha || null, llegaHora: e.llega_hora || null,
    }));
  return {
    llegadaBV: cotizacion.llegada,
    noches: salidaN - diaNum(cotizacion.llegada),
    bus: { saleIso: omn.salidaIso, sale: h.sale, llegaIso: isoDeDiaNum(salidaN + n0(h.mas_dias)), llega: h.llega },
    vuelo: {
      diaIso: opcion.dia_vuelo, sale: opcion.hora_vuelo, llega: opcion.hora_llegada_vuelo || null, ciudad: opcion.ciudad || '',
      masDias: esNum(opcion.llegada_mas_dias) ? Number(opcion.llegada_mas_dias) : null, // null = automático
    },
    extras,
  };
}

// Mejor opción del cliente directo = la de mayor ganancia. Con un revendedor
// manda el precio más bajo para él (empate: la primera).
export function mejorOpcion(resultados) {
  let best = -1;
  resultados.forEach((r, i) => {
    if (r.ganancia === null) return;
    if (best === -1) { best = i; return; }
    const mejor = r.tipo === 'revendedor' ? r.precioFinal < resultados[best].precioFinal : r.ganancia > resultados[best].ganancia;
    if (mejor) best = i;
  });
  return best;
}

// Filas automáticas de una cotización nueva: llegada + 2 … llegada + 7.
export function diasAutomaticos(llegadaIso) {
  return [2, 3, 4, 5, 6, 7].map((n) => sumarDias(llegadaIso, n));
}

// Conversor de dólares: tasas de 4,6 a 5,0 en pasos de 0,1.
export const TASAS_CONVERSOR = [4.6, 4.7, 4.8, 4.9, 5.0];
